import { Injectable, Injector } from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  BfxBlotterAction,
  BfxBlotterOrdersContextMenuItem,
  BfxBlotterOrdersContextMenuPromiseService,
  ContextMenuItemState,
  OrderDataPromiseLookupService,
  SystemConfigPromiseService,
  TraderGroupPromiseLookupService,
  ActiveUserProfilePromiseService,
} from "shared/cubiq-services";

import {
  BfxBlotterActionEvent,
  BfxLadderBlotterContextMenuEventType,
} from "../bfx-base-blotter/bfx-base-blotter-contextmenu.service";
import {
  BfxBlotterOrdersContextMenuService,
  BfxLadderBlotterOrdersContextMenuEvent,
} from "../bfx-blotter-orders/bfx-blotter-orders-contextmenu.service";
import { BfxOrderDetailsContextMenuState } from "./model/BfxOrderDetailsContextMenuState";

export class BfxOrderDetailsContextMenuEvent extends BfxLadderBlotterOrdersContextMenuEvent {}

const ORDER_DETAILS_ACTION_IDS: BfxBlotterAction[] = [
  BfxBlotterAction.ACTION_ORDER_REACTIVATE,
  BfxBlotterAction.ACTION_ORDER_MODIFY,
  BfxBlotterAction.ACTION_ORDER_CANCEL,
  BfxBlotterAction.ACTION_ORDER_DUPLICATE,
  BfxBlotterAction.ACTION_ORDER_UNLOCK,
  BfxBlotterAction.ACTION_TRANSFER,
  BfxBlotterAction.ACTION_TAKE,
  BfxBlotterAction.ACTION_FORCE_TAKE,
  BfxBlotterAction.ACTION_ABORT_TRANSFER,
  BfxBlotterAction.ACTION_ORDER_ACCEPT,
  BfxBlotterAction.ACTION_ORDER_REJECT,
  BfxBlotterAction.SEPARATOR,
];
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { BmarkClientOrdersContextMenuService } from "../bfx-bmark-summary/blotters/client-orders/services/bmark-client-orders-context-menu.service";
import { BfxBlotterOrdersActionSubject } from "../bfx-blotter-orders/bfx-blotter-orders-action-subject.module";
import { BfxBaseBlotter } from "../bfx-base-blotter/bfx-base-blotter";

@Injectable()
export class BfxOrderDetailsContextMenuService extends BfxBlotterOrdersContextMenuService {
  public menuItems: Array<BfxBlotterOrdersContextMenuItem>;
  protected menuState = new BfxOrderDetailsContextMenuState();

  constructor(
    orderDataService: OrderDataPromiseLookupService,
    systemConfigService: SystemConfigPromiseService,
    traderGroupService: TraderGroupPromiseLookupService,
    activeUserService: ActiveUserProfilePromiseService,
    contextMenuService: BfxBlotterOrdersContextMenuPromiseService,
    actionSubject: BfxBlotterOrdersActionSubject,
    private injector: Injector,
  ) {
    super(
      orderDataService,
      systemConfigService,
      traderGroupService,
      activeUserService,
      contextMenuService,
      actionSubject,
    );
  }

  public fireActionEvent(
    actionId: BfxBlotterAction,
    ladderBlotter: any,
    selectedIds?: Array<string>,
    selectedItems?: Array<any>,
    itemState?: ContextMenuItemState,
    hide?: boolean,
    menuBoundingClientRect?: any,
    actionEvent?: any,
  ): void {
    let event: BfxBlotterActionEvent = new BfxBlotterActionEvent(
      actionId,
      ladderBlotter,
      this.getSelectedIds(selectedIds, ladderBlotter),
      this.getSelectedItems(selectedItems, ladderBlotter),
      itemState,
      hide,
      menuBoundingClientRect,
      actionEvent,
    );

    if (
      (actionId == BfxBlotterAction.ACTION_TRANSFER || actionId == BfxBlotterAction.ACTION_TAKE) &&
      event.selectedItems != null &&
      event.selectedItems.find(
        (item: CubiqTS.OrderDataImpl) => item.orderType != CubiqTS.OrderType.BENCHMARK,
      ) == null
    ) {
      event.selectedItems = event.selectedItems.map((order: CubiqTS.OrderDataImpl) => {
        if (order.allocationData != null && order.allocationData.length == 1) {
          let rootAllocation: CubiqTS.AllocationDataImpl = order.allocationData[0];
          if (
            rootAllocation.componentAllocations != null &&
            rootAllocation.componentAllocations.length == 2
          ) {
            let allocationData: CubiqTS.AllocationDataImpl[] =
              rootAllocation.componentAllocations.filter((allocation: CubiqTS.AllocationDataImpl) =>
                this.menuState.activeUser.canSeeBmarkAllocation(order, allocation),
              );
            order = Object.assign(new CubiqTS.OrderDataImpl(), order);
            order.allocationData = [
              Object.assign(new CubiqTS.AllocationDataImpl(), rootAllocation),
            ];
            order.allocationData[0].componentAllocations = allocationData;
          }
        }

        return order;
      });
    }

    if (
      actionId == BfxBlotterAction.ACTION_ORDER_DUPLICATE &&
      selectedIds != null &&
      event.selectedIds == null
    ) {
      event.selectedIds = selectedIds;
    }

    this.actionSubject.next(event);
  }

  public buildMenu(
    blotter: BfxBaseBlotter,
    pageX: number,
    pageY: number,
    selectedIds: Array<string>,
    selectedItems?: Array<object> | null,
  ): void {
    let response = this.contextMenuService.getMenuItemsForDataItem(
      selectedItems[0] as CubiqTS.OrderDataImpl,
      this.menuState,
    );
    let menuItems = this.postProcessContextMenus(
      response.menuItems as Array<BfxBlotterOrdersContextMenuItem>,
      selectedItems,
    );

    menuItems = menuItems.filter(
      (menuItem: BfxBlotterOrdersContextMenuItem) =>
        ORDER_DETAILS_ACTION_IDS.indexOf(menuItem.actionId) >= 0,
    );

    this.menuItems = this.reduceVisibleMenuItems(menuItems);

    let actionDecorators: any = response.actionDecorators;
    this.showMenuSubject.next(
      new BfxLadderBlotterOrdersContextMenuEvent(
        BfxLadderBlotterContextMenuEventType.MENU_SHOW,
        blotter,
        pageX,
        pageY,
        [],
        actionDecorators,
        this.menuItems,
      ),
    );
  }

  protected postProcessContextMenus(
    menuItems: Array<BfxBlotterOrdersContextMenuItem>,
    selectedItems: Array<any>,
  ): Array<BfxBlotterOrdersContextMenuItem> {
    menuItems.forEach((element: BfxBlotterOrdersContextMenuItem) => {
      if (element.actionId == BfxBlotterAction.ACTION_ORDER_CANCEL) {
        element.state.iconClass = "icon-menu-error";
      } else if (element.actionId == BfxBlotterAction.ACTION_ORDER_DUPLICATE) {
        let order: CubiqTS.OrderData = selectedItems[0] as CubiqTS.OrderData;
        element.state.enabled =
          element.isEnabled &&
          ((order.orderType != CubiqTS.OrderType.BENCHMARK &&
            order.orderType != CubiqTS.OrderType.BMALGO) ||
            (order.orderType == CubiqTS.OrderType.BENCHMARK &&
              order.fixingDetails != null &&
              order.fixingDetails.bmOrderType == CubiqTS.BenchmarkOrderType.Standard));
      } else if (element.actionId == BfxBlotterAction.ACTION_TRANSFER) {
        let order: CubiqTS.OrderData = selectedItems[0] as CubiqTS.OrderData;
        if (
          order.orderType == CubiqTS.OrderType.BENCHMARK ||
          order.orderType == CubiqTS.OrderType.BMALGO
        ) {
          let clientOrdersContextMenuService: BmarkClientOrdersContextMenuService =
            this.injector.get(BmarkClientOrdersContextMenuService);
          element.state.enabled = clientOrdersContextMenuService.checkTransferEnabled(order);
          element.state.count++;
        }
      } else if (element.actionId == BfxBlotterAction.ACTION_TAKE) {
        let order: CubiqTS.OrderData = selectedItems[0] as CubiqTS.OrderData;
        if (
          order.orderType == CubiqTS.OrderType.BENCHMARK ||
          order.orderType == CubiqTS.OrderType.BMALGO
        ) {
          let clientOrdersContextMenuService: BmarkClientOrdersContextMenuService =
            this.injector.get(BmarkClientOrdersContextMenuService);
          element.state.enabled = clientOrdersContextMenuService.checkTakeEnabled(order);
          element.state.count++;
        }
      } else if (element.actionId == BfxBlotterAction.ACTION_ABORT_TRANSFER) {
        let order: CubiqTS.OrderData = selectedItems[0] as CubiqTS.OrderData;
        if (
          order.orderType == CubiqTS.OrderType.BENCHMARK ||
          order.orderType == CubiqTS.OrderType.BMALGO
        ) {
          element.state.available = false;
        }
      } else if (element.actionId == BfxBlotterAction.ACTION_ORDER_MODIFY) {
        let order: CubiqTS.OrderData = selectedItems[0] as CubiqTS.OrderData;
        if (order.orderType == CubiqTS.OrderType.BENCHMARK) {
          if (order.fixingDetails?.bmOrderType != CubiqTS.BenchmarkOrderType.PositionOrder) {
            let clientOrdersContextMenuService: BmarkClientOrdersContextMenuService =
              this.injector.get(BmarkClientOrdersContextMenuService);
            element.state.enabled = clientOrdersContextMenuService.checkModifyEnabled(order);
            element.state.count++;
          } else {
            element.state.enabled = false;
          }
        } else if (order.orderType == CubiqTS.OrderType.BMALGO) {
          element.state.enabled = false;
        } else if (order.orderState === CubiqTS.OrderState.SAVED) {
          element.state.enabled = false; // Disabling 'Modify' for scratchpad, after scrum discussion
        }
      } else if (element.actionId == BfxBlotterAction.ACTION_FORCE_TAKE) {
        let order: CubiqTS.OrderData = selectedItems[0] as CubiqTS.OrderData;
        if (
          order.orderType == CubiqTS.OrderType.BENCHMARK ||
          order.orderType == CubiqTS.OrderType.BMALGO
        ) {
          element.state.enabled = false;
        }
      }
    });

    return menuItems;
  }

  public setSelectedDIEventIds(selectedDIEventIds: any[]): void {
    this.menuState.selectedDIEventIds = selectedDIEventIds;
  }
}
